"use client"

import React, { useMemo, useState } from 'react'
import { Column, Table } from '@tanstack/react-table'
import { Check, PlusCircle, Search, X } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Separator } from '../ui/separator'
import { cn } from '../../lib/utils'

export interface FacetedFilterOption {
  value: string
  label?: string
  icon?: React.ReactNode
}

interface DataTableFacetedFilterProps<TData> {
  table: Table<TData>
  column: string
  title?: string
  options?: FacetedFilterOption[] | "auto"
  data: TData[]
}

export function DataTableFacetedFilter<TData>({
  table,
  column: columnId,
  title,
  options = "auto",
  data,
}: DataTableFacetedFilterProps<TData>) {
  const column = table.getColumn(columnId)
  const [searchQuery, setSearchQuery] = useState("")

  if (!column) {
    return null
  }

  const columnDef = column.columnDef as any
  const filterValueAccessor = columnDef?.meta?.filterValueAccessor
  const filterOptions = columnDef?.meta?.filterOptions

  // Get filter value from row
  const getFilterValue = (row: TData) => {
    if (filterValueAccessor) {
      return filterValueAccessor(row)
    }
    // Try to get value from row
    const value = (row as any)[columnId]
    return value
  }

  // Auto-detect unique values and calculate counts
  const { availableOptions, optionCounts } = useMemo(() => {
    const counts: Record<string, number> = {}
    const uniqueValues = new Set<string>()

    // Count all values in data
    data.forEach(row => {
      const value = getFilterValue(row)
      if (value != null && value !== '') {
        const strValue = String(value)
        uniqueValues.add(strValue)
        counts[strValue] = (counts[strValue] || 0) + 1
      }
    })

    let finalOptions: FacetedFilterOption[] = []

    if (options !== "auto") {
      // Use provided options
      finalOptions = options
    } else if (filterOptions) {
      // Use column meta options
      finalOptions = filterOptions.map((opt: string) => ({ value: opt, label: opt }))
    } else {
      // Auto-detect from data
      finalOptions = Array.from(uniqueValues)
        .sort()
        .slice(0, 50) // Limit to 50 options
        .map(value => ({ value, label: value }))
    }

    return { availableOptions: finalOptions, optionCounts: counts }
  }, [data, options, filterOptions])

  // Get current filter value
  const filterValue = column.getFilterValue() as string[] | undefined
  const selectedValues = new Set(filterValue || [])

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return availableOptions

    return availableOptions.filter(option => {
      const label = option.label || option.value
      return label.toLowerCase().includes(searchQuery.toLowerCase())
    })
  }, [availableOptions, searchQuery])

  // Get display title from props, column header, or column id
  const displayTitle = title || (column.columnDef as any).header || columnId

  const handleSelect = (value: string) => {
    const newValues = new Set(selectedValues)
    if (newValues.has(value)) {
      newValues.delete(value)
    } else {
      newValues.add(value)
    }

    if (newValues.size === 0) {
      column.setFilterValue(undefined)
    } else {
      column.setFilterValue(Array.from(newValues))
    }
  }

  const handleClearAll = () => {
    column.setFilterValue(undefined)
    setSearchQuery("")
  }

  const handleSelectAll = () => {
    const allValues = filteredOptions.map(opt => opt.value)
    column.setFilterValue(allValues)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 border-dashed",
            selectedValues.size > 0 && "border-solid"
          )}
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          {displayTitle}
          {selectedValues.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  Array.from(selectedValues)
                    .slice(0, 2)
                    .map(value => {
                      const option = availableOptions.find(opt => opt.value === value)
                      return (
                        <Badge
                          variant="secondary"
                          key={value}
                          className="rounded-sm px-1 font-normal"
                        >
                          {option?.label || value}
                        </Badge>
                      )
                    })
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${displayTitle.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8"
            />
          </div>
        </div>
        
        <Separator />
        
        <div className="max-h-[300px] overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No results found
            </div>
          ) : (
            <div className="p-2">
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.has(option.value)
                const count = optionCounts[option.value] || 0
                
                return (
                  <Button
                    key={option.value}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => handleSelect(option.value)}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <Check className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <span className="mr-2">{option.icon}</span>
                    )}
                    <span className="flex-1 text-left">
                      {option.label || option.value}
                    </span>
                    {count > 0 && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {count}
                      </span>
                    )}
                  </Button>
                )
              })}
            </div>
          )}
        </div>

        {(selectedValues.size > 0 || filteredOptions.length > 0) && (
          <>
            <Separator />
            <div className="flex items-center justify-between p-2">
              {selectedValues.size > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="h-8 px-2 lg:px-3"
                >
                  Clear
                  <X className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="h-8 px-2 lg:px-3"
                >
                  Select all
                  <Check className="ml-2 h-4 w-4" />
                </Button>
              )}
              <span className="text-xs text-muted-foreground">
                {selectedValues.size} of {availableOptions.length} selected
              </span>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}